/**
 * ClubsPage - Club Discovery & Directory
 * 
 * Public directory with search, filters, and grid layout for club discovery.
 * Rebuilt to match Figma Nightfall Kit design.
 * 
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7D
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { TopBar, Input, EmptyState, ClubCardSkeleton } from '../components';
import { useClubs } from '../hooks/useClubs';
import { createClubsMobileMenu } from '../components/MobileContextMenu';
import { Search, Users, Calendar, MapPin, Plus } from 'lucide-react';

type FilterTab = 'all' | 'joined' | 'popular';

// Club Card Component for grid
interface ClubCardProps {
  club: {
    id: string;
    name: string;
    description?: string | null;
    slug: string;
    member_count: number;
    upcoming_event_count?: number;
    role?: 'leader' | 'member' | 'moderator' | 'owner';
    hero_image_url?: string | null;
    activity_type?: string;
  };
  onClick: () => void;
}

function ClubCard({ club, onClick }: ClubCardProps) {
  const isJoined = club.role === 'leader' || club.role === 'member';
  
  return (
    <div
      className="bg-oro-surface border border-oro-border rounded-2xl overflow-hidden cursor-pointer hover:border-accent/30 transition-colors shadow-card"
      onClick={onClick}
    >
      {/* Hero Image */}
      <div className="relative h-32 bg-oro-surface-alt">
        {club.hero_image_url ? (
          <img 
            src={club.hero_image_url} 
            alt={club.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-text-muted">
            <Users className="w-8 h-8" />
          </div>
        )}
        {isJoined && (
          <div className="absolute top-3 right-3 px-2 py-1 bg-accent/20 text-accent text-xs font-medium rounded-full">
            Joined
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-base text-text-primary font-medium mb-2 line-clamp-2">
          {club.name}
        </h3>

        <p className="text-sm text-text-muted mb-3 line-clamp-2">
          {club.description || 'No description available'}
        </p>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <Users className="w-4 h-4 shrink-0" />
            <span>{club.member_count} {club.member_count === 1 ? 'member' : 'members'}</span>
          </div>

          {club.upcoming_event_count !== undefined && club.upcoming_event_count > 0 && (
            <div className="flex items-center gap-2 text-xs text-accent">
              <Calendar className="w-4 h-4 shrink-0" />
              <span>{club.upcoming_event_count} upcoming {club.upcoming_event_count === 1 ? 'event' : 'events'}</span>
            </div>
          )}

          {club.activity_type && (
            <div className="flex items-center gap-2 text-xs text-text-muted">
              <MapPin className="w-4 h-4 shrink-0" />
              <span className="capitalize">{club.activity_type}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ClubsPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { data: clubs, isLoading, error } = useClubs();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');

  // Create mobile menu configuration
  const clubsMobileMenu = createClubsMobileMenu(navigate, logout);

  // Filter and search clubs
  const filteredClubs = useMemo(() => {
    if (!clubs) return [];
    
    let filtered = clubs;
    
    // Apply filter
    if (activeFilter === 'joined') {
      filtered = filtered.filter(club => club.role === 'leader' || club.role === 'member');
    } else if (activeFilter === 'popular') {
      filtered = filtered.sort((a, b) => (b.member_count || 0) - (a.member_count || 0));
    }
    
    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(club =>
        club.name.toLowerCase().includes(query) ||
        (club.description && club.description.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  }, [clubs, searchQuery, activeFilter]);

  return (
    <div className="flex flex-col bg-oro-bg">
      <TopBar title="Clubs" mobileMenu={clubsMobileMenu} />
      
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-6 space-y-6">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <Input
              type="text"
              placeholder="Search clubs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              size="lg"
            />
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 border-b border-oro-border -mx-4 px-4">
            {[
              { key: 'all' as const, label: 'All' },
              { key: 'joined' as const, label: 'Joined' },
              { key: 'popular' as const, label: 'Popular' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveFilter(tab.key)}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeFilter === tab.key
                    ? 'text-accent border-b-2 border-accent'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <ClubCardSkeleton key={i} />
              ))}
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="py-12">
              <EmptyState
                icon="⚠️"
                title="Failed to load clubs"
                message={error instanceof Error ? error.message : 'Something went wrong'}
                actionLabel='Try Again' onAction={() => window.location.reload()}
              />
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && filteredClubs.length === 0 && (
            <div className="py-12">
              <EmptyState
                icon="🏃‍♂️"
                title={searchQuery ? 'No clubs found' : activeFilter === 'joined' ? 'No joined clubs' : 'No clubs available'}
                message={
                  searchQuery 
                    ? `No clubs match "${searchQuery}"`
                    : activeFilter === 'joined' 
                    ? 'Join some clubs to see them here'
                    : 'Be the first to create a club in your area'
                }
                actionLabel='Create Club' onAction={() => navigate('/clubs/create')}
              />
            </div>
          )}

          {/* Clubs Grid */}
          {!isLoading && !error && filteredClubs.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredClubs.map((club) => (
                <ClubCard
                  key={club.id}
                  club={club}
                  onClick={() => navigate(`/clubs/${club.slug}`)}
                />
              ))}
            </div>
          )}

          {/* Create Club FAB */}
          <button
            onClick={() => navigate('/clubs/create')}
            className="fixed bottom-24 left-4 w-11 h-11 bg-accent hover:bg-accent/90 text-black rounded-2xl shadow-float flex items-center justify-center transition-all duration-200 hover:shadow-float-lg"
            aria-label="Create Club"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
}
